import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ClanLevelBadge from './components/clan-level-badge';
import ClanQuarterLeaderboard from './components/clan-quarter-leaderboard';
import {
  getLeaderboard,
  getMyClan,
  createClan,
  getSeasonStatus,
  registerForSeason,
  getYearlyLeaderboard,
  CLAN_SEASON_STATE_LABEL,
  ClanSummary,
  Clan,
  ClanSeason,
  ClanYearly,
} from '../../api/group/clan.api';

const BTN = 'rounded-full bg-[#066bff] px-5 py-2 text-[15px] font-medium text-white transition hover:brightness-105 disabled:opacity-60';
const CARD = 'flex flex-col overflow-hidden rounded-[20px] bg-white dark:bg-dark2 shadow-[4px_4px_44px_rgba(0,0,0,0.08)]';

// % tiến độ mùa của bang = điểm mùa / mốc mục tiêu (SeasonGoal), chặn trần 100.
const seasonPercent = (c?: ClanSummary): number => {
  if (!c || !c.seasonGoal || c.seasonGoal <= 0) return 0;
  return Math.min(100, Math.round((c.seasonScore / c.seasonGoal) * 100));
};

const clanInitial = (name?: string | null) => (name?.trim()?.[0] || '?').toUpperCase();

// Avatar tròn của bang. Thiếu bang (slot trống) → vòng nét đứt nhạt cho nhẹ.
const ClanAvatar: React.FC<{ clan?: ClanSummary; size: number; ring?: string }> = ({ clan, size, ring }) => {
  if (!clan) {
    return (
      <div
        className="flex items-center justify-center rounded-full border-2 border-dashed border-[#c7d4e6] bg-[#eef3fa] font-semibold text-[#aab7c9] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-500"
        style={{ width: size, height: size, fontSize: size * 0.3 }}
      >
        ?
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-center overflow-hidden rounded-full bg-cover bg-center font-bold text-white shadow-[0_10px_28px_rgba(6,107,255,0.18)]"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.34,
        border: `4px solid ${ring || '#fff'}`,
        background: clan.logo
          ? `url(${clan.logo}) center/cover`
          : `linear-gradient(135deg, ${clan.crestColor || '#066bff'}, #19052f)`,
      }}
    >
      {!clan.logo && clanInitial(clan.name)}
    </div>
  );
};

// Thanh tiến độ mùa kiểu slider: fill = tiến độ đạt được, có chấm + núm tròn ở cuối fill.
// Thanh tiến độ mùa (đúng Figma): track #E0E0E0, fill #0043CE, 4 chấm 4px, KHÔNG núm.
const BAR_DOTS = [14, 31, 62, 92];
const SeasonBar: React.FC<{ pct: number }> = ({ pct }) => (
  <div className="relative mt-2 h-[12px] w-full overflow-hidden rounded-full bg-[#e0e0e0] dark:bg-slate-600">
    <div className="absolute left-0 top-0 h-full rounded-full bg-[#0043ce]" style={{ width: `${pct}%` }} />
    {BAR_DOTS.map((d) => (
      <span
        key={d}
        className="absolute top-1/2 size-[4px] -translate-y-1/2 rounded-full"
        style={{ left: `${d}%`, background: pct >= d ? 'rgba(255,255,255,0.9)' : '#bcc3ce' }}
      />
    ))}
  </div>
);

// Màu mặt trước từng bậc — lấy nguyên gradient từ Figma (node 243:7679).
const FRONT_GRADIENT: Record<1 | 2 | 3, string> = {
  1: 'linear-gradient(0deg, #afd0ff 33%, #fff9f5 79%, #81b4ff 100%)',
  2: 'linear-gradient(151.73deg, #e5f5ff 0%, #9cc5ff 58%, #74abfa 108%)',
  3: 'linear-gradient(133.86deg, #bed8ff 13%, #aecfff 66%, #5197ff 124%)',
};
// Cạnh phải — xanh nhạt, chỉ hơi đậm hơn mặt trước để gợi khối nhẹ (theo Figma: airy, không tối).
const SIDE_COLOR: Record<1 | 2 | 3, string> = { 1: '#8fbcf6', 2: '#88b6f4', 3: '#82b1f2' };

// Khối bục 3D: mặt trên nghiêng (lid) + cạnh phải + mặt trước có số. DEPTH = độ dày phối cảnh.
const DEPTH = 20;
const PodiumBlock: React.FC<{ place: 1 | 2 | 3; width: number; height: number }> = ({ place, width, height }) => (
  <div className="relative mt-12" style={{ width, height, zIndex: place === 1 ? 3 : 1 }}>
    {/* mặt trên (parallelogram lùi về sau-phải) */}
    <div
      className="absolute left-0"
      style={{
        top: -DEPTH,
        width: width + DEPTH,
        height: DEPTH,
        background: '#edf5ff',
        clipPath: `polygon(${DEPTH}px 0, 100% 0, calc(100% - ${DEPTH}px) 100%, 0 100%)`,
      }}
    />
    {/* cạnh phải */}
    <div
      className="absolute"
      style={{
        left: width,
        top: -DEPTH,
        width: DEPTH,
        height: height + DEPTH,
        background: SIDE_COLOR[place],
        clipPath: `polygon(0 ${DEPTH}px, 100% 0, 100% calc(100% - ${DEPTH}px), 0 100%)`,
      }}
    />
    {/* mặt trước + số */}
    <div
      className="absolute inset-x-0 bottom-0 flex items-center justify-center rounded-t-[4px]"
      style={{ height, background: FRONT_GRADIENT[place] }}
    >
      <span
        className="font-extrabold leading-none text-white drop-shadow-[0_3px_8px_rgba(6,107,255,0.25)]"
        style={{ fontSize: place === 1 ? 96 : 66 }}
      >
        {place}
      </span>
    </div>
  </div>
);

// Một vị trí trên bục: avatar + (vương miện nếu #1) + tên + thanh tiến độ + khối bậc 3D.
// Tỉ lệ theo Figma: bậc 1 hẹp-cao, bậc 2/3 rộng-thấp.
const PodiumSpot: React.FC<{ clan?: ClanSummary; place: 1 | 2 | 3 }> = ({ clan, place }) => {
  const blockW = place === 1 ? 196 : 242;
  const blockH = place === 1 ? 276 : place === 2 ? 196 : 162;
  const avatarSize = place === 1 ? 138 : 126;
  const barW = place === 1 ? 182 : 210;
  // Dạt cụm avatar+tên+thanh ra ngoài (bậc 2 sang trái, bậc 3 sang phải) để thanh không dính cột giữa.
  const floatShift = place === 2 ? -26 : place === 3 ? 26 : 0;
  const pct = seasonPercent(clan);

  return (
    <div className="flex flex-col items-center" style={{ width: blockW }}>
      {/* Cụm nổi: avatar + tên + thanh tiến độ (dạt ra ngoài theo floatShift) */}
      <div className="flex flex-col items-center" style={{ transform: `translateX(${floatShift}px)` }}>
        {/* Avatar + vương miện */}
        <div className="relative flex flex-col items-center" style={{ width: avatarSize }}>
          {place === 1 && clan && (
            <span
              className="absolute -top-5 left-1 z-10 text-[38px] leading-none drop-shadow"
              style={{ transform: 'rotate(-22deg)' }}
              aria-hidden
            >
              👑
            </span>
          )}
          <ClanAvatar clan={clan} size={avatarSize} ring={place === 1 ? '#ffd34d' : '#fff'} />
        </div>

        {/* Tên bang */}
        <div className="mt-3 max-w-full truncate px-1 text-center text-[22px] font-semibold text-[#252527] dark:text-white">
          {clan?.name || 'Chưa có'}
        </div>

        {/* Tiến độ mùa: số = phần còn lại "to complete" (theo Figma), bar = tiến độ đạt được */}
        <div className="relative z-20 mt-1 flex max-w-full flex-col items-center" style={{ width: barW }}>
          <span className="flex items-center gap-1.5 text-[15px] font-bold text-[#0072c3]">
            <span>{100 - pct}%</span>
            <span className="font-semibold">còn lại</span>
          </span>
          <SeasonBar pct={pct} />
        </div>
      </div>

      {/* Khối bậc 3D */}
      <PodiumBlock place={place} width={blockW} height={blockH} />
    </div>
  );
};

const ClanListPage: React.FC = () => {
  const navigate = useNavigate();
  const [clans, setClans] = useState<ClanSummary[]>([]);
  const [myClan, setMyClan] = useState<Clan | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slogan: '', crestColor: '#066bff' });
  const [season, setSeason] = useState<ClanSeason | null>(null);
  const [yearly, setYearly] = useState<ClanYearly[]>([]);
  const [registering, setRegistering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, mine, ss, yr] = await Promise.all([
        getLeaderboard(100),
        getMyClan().catch(() => null),
        getSeasonStatus().catch(() => null),
        getYearlyLeaderboard().catch(() => [] as ClanYearly[]),
      ]);
      setClans(list || []);
      setMyClan(mine);
      setSeason(ss);
      setYearly(yr || []);
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được danh sách bang hội');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const s = await registerForSeason();
      setSeason(s);
      toast.success('Đã đăng ký bang tham gia mùa giải!');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Đăng ký không thành công');
    } finally {
      setRegistering(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Nhập tên bang hội');
      return;
    }
    setCreating(true);
    try {
      const id = await createClan({ name: form.name.trim(), slogan: form.slogan, crestColor: form.crestColor });
      toast.success('Đã tạo bang hội');
      setShowCreate(false);
      navigate(`/clan/${id}`);
    } catch (e: any) {
      toast.error(e?.message || 'Tạo bang hội không thành công');
    } finally {
      setCreating(false);
    }
  };

  // BXH đã xếp theo điểm mùa giảm dần → 3 bang đầu lên bục (bố cục 2 - 1 - 3).
  const top1 = clans[0];
  const top2 = clans[1];
  const top3 = clans[2];

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-6 py-6 pb-16">
      {/* Nút vào bang hội của tôi — luôn ở đầu để truy cập nhanh */}
      {myClan && (
        <button
          onClick={() => navigate(`/clan/${myClan.id}`)}
          className="flex items-center justify-between gap-3 rounded-[16px] bg-[#066bff] px-6 py-4 text-left text-white shadow-[0_6px_20px_rgba(6,107,255,0.35)] transition hover:bg-[#0057d6]"
        >
          <span className="flex items-center gap-3">
            <span className="grid size-11 place-items-center rounded-full bg-white/20 text-2xl">🛡️</span>
            <span>
              <span className="block text-[13px] text-white/80">Clan của bạn</span>
              <span className="block text-[19px] font-bold">{myClan.name}</span>
            </span>
          </span>
          <span className="whitespace-nowrap rounded-full bg-white/20 px-4 py-2 text-sm font-semibold">
            Vào bang hội của tôi →
          </span>
        </button>
      )}

      {/* Bảng xếp hạng War Clan — bục 1-2-3 */}
      <section className="relative overflow-hidden rounded-[24px] bg-gradient-to-b from-[#f2f8ff] to-white p-8 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:from-slate-800 dark:to-slate-800 dark:shadow-none">
        <h1 className="relative z-10 text-[26px] font-semibold tracking-tight text-[#19052f] dark:text-white md:text-[30px]">
          Bảng xếp hạng War clan
        </h1>

        {/* Vòng tròn đồng tâm trang trí */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-24 flex items-center justify-center">
          <span className="absolute size-[640px] rounded-full border border-[#e3eeff] dark:border-slate-700/60" />
          <span className="absolute size-[470px] rounded-full border border-[#e3eeff] dark:border-slate-700/60" />
          <span className="absolute size-[310px] rounded-full border border-[#e6f0ff] dark:border-slate-700/50" />
        </div>

        {loading ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <Spin />
          </div>
        ) : clans.length === 0 ? (
          <p className="relative z-10 mt-10 text-center text-[15px] text-[#657384] dark:text-slate-400">
            🏆 Chưa có bang hội nào tranh hạng. Hãy tạo bang đầu tiên!
          </p>
        ) : (
          <div className="relative z-10 mt-16 flex justify-center overflow-x-auto">
            <div className="flex w-fit origin-top scale-[0.5] items-end justify-center gap-0 sm:scale-90 md:scale-100">
              <PodiumSpot clan={top2} place={2} />
              <PodiumSpot clan={top1} place={1} />
              <PodiumSpot clan={top3} place={3} />
            </div>
          </div>
        )}
      </section>

      {/* Trạng thái mùa giải + đăng ký thi đấu */}
      {season && (
        <section className="rounded-[20px] border border-[#066bff]/20 bg-gradient-to-r from-[#eef5ff] to-white p-5 dark:border-slate-700 dark:from-slate-800 dark:to-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[26px] leading-none">🏆</span>
              <div>
                <div className="text-[12px] text-[#657384] dark:text-slate-400">Mùa giải {season.month || ''}</div>
                <div className="text-[17px] font-semibold text-[#19052f] dark:text-white">
                  {CLAN_SEASON_STATE_LABEL[season.state]}
                  <span className="ml-2 text-[13px] font-normal text-[#657384] dark:text-slate-400">
                    · {season.registeredCount} bang đã đăng ký
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {season.state === 0 && season.remaining > 0 && (
                <span className="rounded-full bg-[#fff4e5] px-3 py-1.5 text-[13px] font-medium text-[#c77700] dark:bg-[#3a2f16] dark:text-[#f2b968]">
                  Còn thiếu {season.remaining} bang để khởi động
                </span>
              )}
              {season.myClanRegistered ? (
                <span className="rounded-full bg-[#e8f7ee] px-3 py-1.5 text-[13px] font-medium text-[#1a8a4a] dark:bg-[#14311f] dark:text-[#5fd08f]">
                  ✓ Bang của bạn đã đăng ký
                </span>
              ) : (
                season.state === 0 &&
                myClan && (
                  <button className={BTN} onClick={handleRegister} disabled={registering}>
                    {registering ? 'Đang đăng ký…' : 'Đăng ký thi đấu'}
                  </button>
                )
              )}
            </div>
          </div>
        </section>
      )}

      {/* Danh sách bang */}
      <section className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h2 className="text-[24px] font-semibold tracking-tight text-[#19052f] dark:text-white">Clan &amp; War Clan</h2>
          {!myClan && (
            <button className={BTN} onClick={() => setShowCreate(true)}>
              + Tạo bang hội
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Spin />
          </div>
        ) : clans.length === 0 ? (
          <div className="rounded-[20px] bg-white dark:bg-dark2 p-10 text-center text-[#657384] dark:text-slate-400 shadow-[4px_4px_44px_rgba(0,0,0,0.05)]">
            Chưa có bang hội nào. Hãy là người đầu tiên tạo bang!
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {clans.map((c) => (
              <div key={c.id} className={CARD}>
                <div
                  className="h-[150px] w-full bg-cover bg-center"
                  style={{
                    background: c.banner
                      ? `url(${c.banner}) center/cover`
                      : `linear-gradient(135deg, ${c.crestColor || '#066bff'}, #19052f)`,
                  }}
                />
                <div className="flex flex-1 flex-col gap-2 p-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-block size-3 rounded-full" style={{ background: c.crestColor || '#066bff' }} />
                    <span className="truncate text-[17px] font-semibold text-[#19052f] dark:text-white">{c.name}</span>
                    <ClanLevelBadge level={c.level} />
                  </div>
                  {c.slogan && <p className="line-clamp-1 text-[13px] text-[#657384] dark:text-slate-400">{c.slogan}</p>}
                  <div className="mt-1 flex items-center gap-1 text-[14px] text-[#19052f] dark:text-white">
                    <span className="text-[#657384] dark:text-slate-400">Thành viên</span>
                    <span className="ml-auto font-semibold">
                      {c.memberCount}/{c.effectiveMemberLimit ?? c.memberLimit}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[14px] text-[#19052f] dark:text-white">
                    <span className="text-[#657384] dark:text-slate-400">Rank</span>
                    <span className="ml-auto font-semibold text-[#066bff]">#{c.rank}</span>
                  </div>
                  <button
                    className={`${BTN} mt-2 w-full`}
                    onClick={() => navigate(`/clan/${c.id}`)}
                  >
                    Xem bang
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bảng xếp hạng quý */}
      <ClanQuarterLeaderboard />

      {/* Bảng vàng năm */}
      {yearly.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-[24px] font-semibold tracking-tight text-[#19052f] dark:text-white">
            🏅 Bảng vàng năm {yearly[0]?.year}
          </h2>
          <div className="overflow-hidden rounded-[20px] bg-white dark:bg-dark2 shadow-[4px_4px_44px_rgba(0,0,0,0.05)]">
            <div className="grid grid-cols-[52px_1fr_110px_96px] gap-2 border-b border-[#eef1f5] px-5 py-3 text-[13px] font-medium text-[#657384] dark:border-slate-700 dark:text-slate-400">
              <span>Hạng</span>
              <span>Clan</span>
              <span className="text-right">Điểm giải</span>
              <span className="text-right">Số lần Top 1</span>
            </div>
            {yearly.map((y) => (
              <div
                key={y.clanId}
                className="grid grid-cols-[52px_1fr_110px_96px] items-center gap-2 border-b border-[#f4f6f9] px-5 py-3 text-[14px] last:border-b-0 dark:border-slate-700/60"
              >
                <span className="text-[16px] font-bold text-[#066bff]">#{y.rank}</span>
                <span className="flex min-w-0 items-center gap-2 font-medium text-[#19052f] dark:text-white">
                  <span
                    className="inline-block size-3 shrink-0 rounded-full"
                    style={{ background: y.crestColor || '#066bff' }}
                  />
                  <span className="truncate">{y.name}</span>
                </span>
                <span className="text-right font-semibold text-[#19052f] dark:text-white">{y.totalLeaguePoints}</span>
                <span className="text-right text-[#657384] dark:text-slate-400">{y.top1Count}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Modal tạo bang */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-[440px] rounded-[20px] bg-white dark:bg-dark2 dark:text-slate-200 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[20px] font-semibold text-[#19052f] dark:text-white">Tạo bang hội</h3>
            <p className="mt-1 text-[13px] text-[#657384] dark:text-slate-400">Cần 1 vé "Tạo bang hội" (mua ở Chợ bằng Ca).</p>
            <div className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[14px]">
                Tên bang hội
                <input
                  className="rounded-lg border border-[#DEE0E6] dark:border-slate-700 dark:border-slate-600 px-3 py-2 outline-none focus:border-[#066bff] dark:bg-slate-700 dark:text-white"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={100}
                  placeholder="VD: Chiến Thần"
                />
              </label>
              <label className="flex flex-col gap-1 text-[14px]">
                Slogan
                <input
                  className="rounded-lg border border-[#DEE0E6] dark:border-slate-700 dark:border-slate-600 px-3 py-2 outline-none focus:border-[#066bff] dark:bg-slate-700 dark:text-white"
                  value={form.slogan}
                  onChange={(e) => setForm({ ...form, slogan: e.target.value })}
                  placeholder="Khẩu hiệu của bang"
                />
              </label>
              <label className="flex items-center gap-2 text-[14px]">
                Màu chủ đạo
                <input
                  type="color"
                  className="h-8 w-12 cursor-pointer rounded border border-[#DEE0E6] dark:border-slate-700 dark:border-slate-600"
                  value={form.crestColor}
                  onChange={(e) => setForm({ ...form, crestColor: e.target.value })}
                />
                <span className="text-[#657384] dark:text-slate-400">{form.crestColor}</span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="rounded-full border border-[#DEE0E6] dark:border-slate-700 dark:border-slate-600 px-5 py-2 text-[15px]" onClick={() => setShowCreate(false)}>
                Huỷ
              </button>
              <button className={BTN} onClick={handleCreate} disabled={creating}>
                {creating ? 'Đang tạo…' : 'Tạo bang'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClanListPage;
